'use client';

/**
 * ChatSurfaceContext — shared state for the registry-driven chat surface.
 *
 * The chat experience decomposes into independently-mounted cards
 * (chat_thread, compose_message) that the registry-driven
 * `<ChatSurface />` paints from the matrix. Cards mount independently,
 * but they need to share:
 *
 *   - the *single* `chat` strategy fetch (one BFF call, not one per card)
 *   - the active sibling tab (for tender chats, the thread filters by
 *     vendor and the composer scopes its post to the same vendor)
 *   - the selected compose context (when the entity has multiple
 *     compose targets — project / RFQ / contact)
 *   - mutation state (sending a WhatsApp invalidates the chat fetch
 *     so the new message lands in the thread)
 *
 * This context is the surface-level shared store. ChatSurface mounts
 * the provider; ChatThreadCard and ComposeMessageCard read from it.
 *
 * Why context (not Zustand): the state is per-surface-instance, scoped
 * to "this chat surface mount", not global. Mounting two surfaces
 * (e.g. one per project, future split-pane) yields two independent
 * conversations naturally. Same shape Insights cards would use if they
 * needed cross-card state.
 *
 * @component_role: Chat Surface State
 * @component_type: React Context Provider
 * @domain: Frontend / Messaging
 * @layer: presentation
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStrategyData, useSendWhatsApp, usePostToChatter } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import type { ContactSummary, MessengerChannel, WhatsAppMediaAttachment } from '@/lib/api/types';

// ── Types — mirror the chat strategy's response shape ─────────────

export interface ChatMessage {
  cdk: string;
  body: string;
  body_translated?: string | null;
  translated_to?: string | null;
  language?: string | null;
  author_name: string;
  author_id: string;
  platform: string;
  message_type: string;
  created_date: string;
  has_attachments: boolean;
  attachment_count: number;
  attachment_urls: string[] | null;
  attachment_metadata: string | null;
  rfq_sid?: string | null;
  rfq_name?: string | null;
  vendor_name?: string | null;
}

export interface ComposeContext {
  label: string;
  model: string;
  id: number;
  type: string;
  sid: string;
}

export interface ChatSibling {
  rfq_sid: string;
  rfq_name: string | null;
  vendor_name: string | null;
}

export interface ChatData {
  entity_type: string;
  entity_sid: string;
  entity_name: string | null;
  messages: ChatMessage[];
  message_count: number;
  contacts: ContactSummary[];
  context_model: string | null;
  context_id: number | null;
  compose_contexts: ComposeContext[];
  channels: MessengerChannel[];
  x_rfq_group_id?: string | null;
  siblings?: ChatSibling[];
}

export const ALL_SIBLINGS = '__all__';

type OdooModel = 'project.project' | 'purchase.order' | 'purchase.requisition' | 'res.partner';

interface ChatSurfaceContextValue {
  // Selection
  entityType: string | null;
  entityId: string | null;

  // Fetch state
  chatData: ChatData | undefined;
  isLoading: boolean;
  error: unknown;

  // Derived
  allMessages: ChatMessage[];
  visibleMessages: ChatMessage[];
  contacts: ContactSummary[];
  composeContexts: ComposeContext[];
  channels: MessengerChannel[];
  siblings: ChatSibling[];
  isTender: boolean;
  countsBySibling: Map<string, number>;

  // Sibling tab (tender)
  activeSibling: string;
  setActiveSibling: (s: string) => void;

  // Context picker
  selectedContextIdx: number;
  setSelectedContextIdx: (i: number) => void;
  activeContext: ComposeContext | null;
  contextModel: OdooModel | null;
  contextId: number | null;

  // Mutations
  sendWhatsApp: (
    phone: string,
    message: string,
    media?: WhatsAppMediaAttachment[],
  ) => Promise<{ success: boolean; error?: string }>;
  postChatter: (message: string) => Promise<{ success: boolean; error?: string }>;
  isSendingWhatsApp: boolean;
  isPostingChatter: boolean;
  invalidateChat: () => void;
}

const ChatSurfaceContext = createContext<ChatSurfaceContextValue | null>(null);

export function useChatSurface(): ChatSurfaceContextValue {
  const ctx = useContext(ChatSurfaceContext);
  if (!ctx) {
    throw new Error(
      'useChatSurface must be used inside a <ChatSurfaceProvider />. ' +
      'Chat cards must mount under <ChatSurface /> — they read shared state from this provider.',
    );
  }
  return ctx;
}

// ── Provider ──────────────────────────────────────────────────────

export function ChatSurfaceProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { entityType, entityId } = useViewportSelection();
  const lang = useLanguageStore((s) => s.lang);

  const sendWhatsAppStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'send_whatsapp'));
  const chatterPostStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'chatter_post'));
  // Chat read — capability-resolved like everything else. Resolves to
  // `'chat'` today via the (messaging, history) registration in
  // `ui_config/handlers/chat.py`. Switching from a literal string
  // means a future rename / replacement of the chat handler is one
  // capability registry change, not a sweep of every consumer.
  const chatHistoryStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'history'));
  const sendWhatsAppMut = useSendWhatsApp(sendWhatsAppStrategy!);
  const postChatterMut = usePostToChatter(chatterPostStrategy!);

  const { data, isLoading, error } = useStrategyData<ChatData>(
    chatHistoryStrategy ?? '',
    { entity_type: entityType ?? '', entity_sid: entityId ?? '', ...(lang ? { lang } : {}) },
    !!chatHistoryStrategy && !!entityType && !!entityId,
  );

  const chatData = data?.data as ChatData | undefined;
  const allMessages = chatData?.messages ?? [];
  const contacts = (chatData?.contacts ?? []) as ContactSummary[];
  const composeContexts = chatData?.compose_contexts ?? [];
  const channels = (chatData?.channels ?? []) as MessengerChannel[];
  const siblings = chatData?.siblings ?? [];
  const isTender = siblings.length > 1;

  const [activeSibling, setActiveSibling] = useState<string>(ALL_SIBLINGS);
  useEffect(() => { setActiveSibling(ALL_SIBLINGS); }, [entityId]);

  const [selectedContextIdx, setSelectedContextIdx] = useState(0);
  useEffect(() => { setSelectedContextIdx(0); }, [entityId]);

  const countsBySibling = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of allMessages) {
      if (m.rfq_sid) counts.set(m.rfq_sid, (counts.get(m.rfq_sid) ?? 0) + 1);
    }
    return counts;
  }, [allMessages]);

  const visibleMessages = useMemo(() => {
    if (!isTender || activeSibling === ALL_SIBLINGS) return allMessages;
    return allMessages.filter(
      (m) => m.rfq_sid === activeSibling || m.rfq_sid == null,
    );
  }, [allMessages, isTender, activeSibling]);

  const activeContext = composeContexts[selectedContextIdx] ?? null;
  const contextModel = (activeContext?.model ?? chatData?.context_model ?? null) as OdooModel | null;
  const contextId = activeContext?.id ?? chatData?.context_id ?? null;

  const invalidateChat = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['strategy-data', 'chat'] });
  }, [queryClient]);

  const sendWhatsApp = useCallback(
    async (phone: string, message: string, media?: WhatsAppMediaAttachment[]) => {
      if (!contextId || !contextModel) return { success: false, error: 'Context not loaded' };
      try {
        const result = await sendWhatsAppMut.mutateAsync({
          to: phone,
          message,
          context_model: contextModel,
          context_id: contextId,
          media: media && media.length > 0 ? media : undefined,
        });
        if (result.success) invalidateChat();
        return { success: result.success, error: result.whatsapp_error };
      } catch (err: unknown) {
        return { success: false, error: err instanceof Error ? err.message : 'Send failed' };
      }
    },
    [contextId, contextModel, sendWhatsAppMut, invalidateChat],
  );

  const postChatter = useCallback(
    async (message: string) => {
      if (!contextId || !contextModel) return { success: false, error: 'Context not loaded' };
      try {
        const result = await postChatterMut.mutateAsync({
          message,
          context_model: contextModel,
          context_id: contextId,
        });
        if (result.success) invalidateChat();
        return result;
      } catch (err: unknown) {
        return { success: false, error: err instanceof Error ? err.message : 'Post failed' };
      }
    },
    [contextId, contextModel, postChatterMut, invalidateChat],
  );

  const value: ChatSurfaceContextValue = {
    entityType,
    entityId,
    chatData,
    isLoading,
    error,
    allMessages,
    visibleMessages,
    contacts,
    composeContexts,
    channels,
    siblings,
    isTender,
    countsBySibling,
    activeSibling,
    setActiveSibling,
    selectedContextIdx,
    setSelectedContextIdx,
    activeContext,
    contextModel,
    contextId,
    sendWhatsApp,
    postChatter,
    isSendingWhatsApp: sendWhatsAppMut.isPending,
    isPostingChatter: postChatterMut.isPending,
    invalidateChat,
  };

  return (
    <ChatSurfaceContext.Provider value={value}>
      {children}
    </ChatSurfaceContext.Provider>
  );
}
